import { useState, useEffect, useRef, useCallback } from 'react'
import { Search, MapPin, Phone, Pill, ChevronRight, X, Star, Edit3, Plus, Building } from 'lucide-react'
import { useI18n } from '../i18n'
import { OrellaSpinner } from './OrellaLoader' // s123: the app's one spinner

const NAVY = '#15354F'
const BLUE = '#46A8F9'
const GREEN = '#66D170'

/**
 * PharmacyLookup — Google Places (New API) powered pharmacy search
 * with manual entry fallback for unlisted pharmacies.
 *
 * Props:
 *   - savedPharmacy: existing pharmacy data
 *   - onSave: (pharmacyData) => void
 */
export default function PharmacyLookup({ savedPharmacy, onSave, allowSkip = true }) {
  const { t } = useI18n()
  const [mode, setMode] = useState(savedPharmacy ? 'saved' : 'search') // 'search' | 'manual' | 'saved'
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [isSearching, setIsSearching] = useState(false)
  const [selectedPharmacy, setSelectedPharmacy] = useState(savedPharmacy || null)
  const [error, setError] = useState(null)
  const [apiReady, setApiReady] = useState(false)

  // Sync when savedPharmacy prop updates (e.g. profile loads after mount)
  useEffect(() => {
    if (savedPharmacy && !selectedPharmacy) {
      setSelectedPharmacy(savedPharmacy)
      setMode('saved')
    }
  }, [savedPharmacy])

  // Manual entry fields
  const [manualName, setManualName] = useState('')
  const [manualAddress, setManualAddress] = useState('')
  const [manualPhone, setManualPhone] = useState('')
  const [manualCity, setManualCity] = useState('')
  const [manualState, setManualState] = useState('')
  const [manualZip, setManualZip] = useState('')

  const searchTimeoutRef = useRef(null)

  useEffect(() => {
    const checkApi = () => {
      if (window.google?.maps?.places?.AutocompleteSuggestion) {
        setApiReady(true)
        return true
      }
      return false
    }
    if (!checkApi()) {
      const interval = setInterval(() => { if (checkApi()) clearInterval(interval) }, 500)
      const timeout = setTimeout(() => clearInterval(interval), 10000)
      return () => { clearInterval(interval); clearTimeout(timeout) }
    }
  }, [])

  const handleSearch = useCallback((value) => {
    setQuery(value)
    setError(null)
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
    if (value.length < 3) { setResults([]); setIsSearching(false); return }

    setIsSearching(true)
    searchTimeoutRef.current = setTimeout(async () => {
      if (!apiReady) { setError(t('errors2.searchLoadingShort')); setIsSearching(false); return }

      const trimmed = value.trim()
      const isZipCode = /^\d{5}$/.test(trimmed)

      // ZIP code search: geocode ZIP to coordinates, then searchNearby for pharmacies
      if (isZipCode) {
        try {
          // Step 1: Geocode ZIP to lat/lng
          const geocoder = new google.maps.Geocoder()
          const geoResult = await new Promise((resolve, reject) => {
            geocoder.geocode(
              { address: trimmed, componentRestrictions: { country: 'US' } },
              (results, status) => {
                if (status === 'OK' && results?.[0]) resolve(results[0])
                else reject(new Error(status))
              }
            )
          })
          const location = geoResult.geometry.location
          const areaName = geoResult.formatted_address || trimmed

          // Step 2: Place.searchNearby (new API, replaces deprecated PlacesService)
          const { places } = await google.maps.places.Place.searchNearby({
            locationRestriction: {
              center: { lat: location.lat(), lng: location.lng() },
              radius: 8000,
            },
            includedPrimaryTypes: ['pharmacy', 'drugstore'],
            fields: ['displayName', 'formattedAddress', 'id', 'rating'],
            maxResultCount: 6,
          })

          if (places?.length > 0) {
            setResults(places.map(p => ({
              placeId: p.id,
              name: p.displayName || 'Pharmacy',
              address: p.formattedAddress || '',
              fullText: `${p.displayName} - ${p.formattedAddress}`,
              rating: p.rating,
            })))
          } else {
            setResults([])
            setError(t('pharmacy.noPharmaciesNear', { area: areaName }))
          }
        } catch (err) {
          console.warn('[PharmacyLookup] ZIP search failed:', err)
          setResults([])
          setError(t('errors2.couldNotSearchZip'))
        }
        setIsSearching(false)
        return
      }

      // Standard name/address search via Autocomplete
      try {
        const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: value,
          includedPrimaryTypes: ['pharmacy', 'drugstore'],
          includedRegionCodes: ['us'],
          language: 'en',
        })
        if (suggestions?.length > 0) {
          setResults(suggestions.slice(0, 5).map(s => {
            const pred = s.placePrediction
            const name = pred.mainText?.text || ''
            const fullText = pred.text?.text || ''
            const address = fullText.startsWith(name) ? fullText.slice(name.length).replace(/^[\s,]+/, '') : pred.secondaryText?.text || ''
            return { placeId: pred.placeId, name, address, fullText }
          }))
        } else {
          setResults([])
        }
      } catch (err) {
        console.warn('[PharmacyLookup] Typed search failed, trying general:', err)
        try {
          const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input: `${value} pharmacy`,
            includedRegionCodes: ['us'],
            language: 'en',
          })
          if (suggestions?.length > 0) {
            setResults(suggestions.slice(0, 5).map(s => {
              const pred = s.placePrediction
              const name = pred.mainText?.text || ''
              const fullText = pred.text?.text || ''
              const address = fullText.startsWith(name) ? fullText.slice(name.length).replace(/^[\s,]+/, '') : pred.secondaryText?.text || ''
              return { placeId: pred.placeId, name, address, fullText }
            }))
          } else { setResults([]) }
        } catch { setResults([]); setError(t('errors2.searchUnavailableShort')) }
      }
      setIsSearching(false)
    }, 400)
  }, [apiReady])

  const selectPlace = async (place) => {
    try {
      const placeObj = new google.maps.places.Place({ id: place.placeId })
      await placeObj.fetchFields({
        fields: ['displayName', 'formattedAddress', 'nationalPhoneNumber', 'internationalPhoneNumber', 'rating', 'websiteURI', 'googleMapsURI', 'location', 'addressComponents'],
      })

      // Extract city/state/zip from address components
      let city = '', state = '', zip = ''
      if (placeObj.addressComponents) {
        for (const comp of placeObj.addressComponents) {
          if (comp.types?.includes('locality')) city = comp.longText || comp.shortText || ''
          if (comp.types?.includes('administrative_area_level_1')) state = comp.shortText || ''
          if (comp.types?.includes('postal_code')) zip = comp.longText || comp.shortText || ''
        }
      }

      const pharmacyData = {
        name: placeObj.displayName || place.name,
        address: placeObj.formattedAddress || place.address,
        phone: placeObj.nationalPhoneNumber || placeObj.internationalPhoneNumber || null,
        city, state, zip,
        rating: placeObj.rating || null,
        website: placeObj.websiteURI || null,
        googleMapsUrl: placeObj.googleMapsURI || null,
        placeId: place.placeId,
        latitude: placeObj.location?.lat(),
        longitude: placeObj.location?.lng(),
        source: 'google_places',
        savedAt: new Date().toISOString(),
      }
      setSelectedPharmacy(pharmacyData)
      setMode('saved')
      setResults([])
      setQuery('')
      onSave?.(pharmacyData)
    } catch (err) {
      console.warn('[PharmacyLookup] fetchFields failed:', err)
      const pharmacyData = { name: place.name, address: place.address, placeId: place.placeId, source: 'google_places', savedAt: new Date().toISOString() }
      setSelectedPharmacy(pharmacyData)
      setMode('saved')
      setResults([])
      setQuery('')
      onSave?.(pharmacyData)
    }
  }

  const handleManualSave = () => {
    if (!manualName.trim()) return
    const pharmacyData = {
      name: manualName.trim(),
      address: manualAddress.trim(),
      phone: manualPhone.trim() || null,
      city: manualCity.trim(),
      state: manualState.trim(),
      zip: manualZip.trim(),
      source: 'manual_entry',
      savedAt: new Date().toISOString(),
    }
    setSelectedPharmacy(pharmacyData)
    setMode('saved')
    onSave?.(pharmacyData)
  }

  const clearSelection = () => {
    setSelectedPharmacy(null)
    setMode('search')
    onSave?.(null)
  }

  // ===== SAVED STATE =====
  if (mode === 'saved' && selectedPharmacy) {
    return (
      <div className="bg-white rounded-2xl border-[1.5px] border-green-200 overflow-hidden">
        <div className="flex items-center gap-2 px-3.5 py-2.5 border-l-[3px] border-l-green-500 bg-green-50">
          <Pill size={15} className="text-green-600" />
          <h4 className="text-[12px] font-bold uppercase tracking-[0.5px] text-green-700">{t('pharmacy.yourPharmacy')}</h4>
          <div className="ml-auto flex items-center gap-1">
            <button onClick={() => setMode('search')} className="p-1 text-green-600 active:text-green-800"><Edit3 size={13} /></button>
            <button onClick={clearSelection} className="p-1 text-red-400 active:text-red-600"><X size={13} /></button>
          </div>
        </div>
        <div className="px-3.5 py-3 space-y-1.5">
          <p className="text-[13px] font-bold" style={{ color: NAVY }}>{selectedPharmacy.name}</p>
          {selectedPharmacy.address && (
            <div className="flex items-start gap-1.5">
              <MapPin size={12} className="text-gray-400 flex-shrink-0 mt-0.5" />
              <p className="text-[11.5px] text-gray-500 leading-snug">{selectedPharmacy.address}</p>
            </div>
          )}
          {selectedPharmacy.phone && (
            <div className="flex items-center gap-1.5">
              <Phone size={12} className="text-gray-400" />
              <a href={`tel:${selectedPharmacy.phone}`} className="text-[11.5px] text-[#46A8F9]">{selectedPharmacy.phone}</a>
            </div>
          )}
        </div>
      </div>
    )
  }

  // ===== MANUAL ENTRY =====
  if (mode === 'manual') {
    return (
      <div className="bg-white rounded-2xl border-[1.5px] border-gray-200 overflow-hidden">
        <div className="flex items-center gap-2 px-3.5 py-2.5 border-l-[3px] border-l-[#46A8F9] bg-[#E9F8FF]">
          <Pill size={15} className="text-[#46A8F9]" />
          <h4 className="text-[12px] font-bold uppercase tracking-[0.5px] text-[#15354F]">{t('pharmacy.enterPharmacy')}</h4>
        </div>
        <div className="p-3.5 space-y-3">
          <input value={manualName} onChange={e => setManualName(e.target.value)} placeholder="Pharmacy name *" className="w-full px-3 py-2.5 rounded-xl border-[1.5px] border-gray-200 text-[13px] placeholder-gray-300 focus:outline-none focus:border-[#46A8F9]" style={{ color: NAVY }} />
          <input value={manualAddress} onChange={e => setManualAddress(e.target.value)} placeholder="Street address" className="w-full px-3 py-2.5 rounded-xl border-[1.5px] border-gray-200 text-[13px] placeholder-gray-300 focus:outline-none focus:border-[#46A8F9]" style={{ color: NAVY }} />
          <div className="flex gap-2">
            <input value={manualCity} onChange={e => setManualCity(e.target.value)} placeholder="City" className="flex-1 px-3 py-2.5 rounded-xl border-[1.5px] border-gray-200 text-[13px] placeholder-gray-300 focus:outline-none focus:border-[#46A8F9]" style={{ color: NAVY }} />
            <input value={manualState} onChange={e => setManualState(e.target.value)} placeholder="State" className="w-16 px-3 py-2.5 rounded-xl border-[1.5px] border-gray-200 text-[13px] placeholder-gray-300 focus:outline-none focus:border-[#46A8F9]" style={{ color: NAVY }} maxLength={2} />
            <input value={manualZip} onChange={e => setManualZip(e.target.value)} placeholder="ZIP" className="w-20 px-3 py-2.5 rounded-xl border-[1.5px] border-gray-200 text-[13px] placeholder-gray-300 focus:outline-none focus:border-[#46A8F9]" style={{ color: NAVY }} maxLength={5} />
          </div>
          <input value={manualPhone} onChange={e => setManualPhone(e.target.value)} placeholder="Phone number" className="w-full px-3 py-2.5 rounded-xl border-[1.5px] border-gray-200 text-[13px] placeholder-gray-300 focus:outline-none focus:border-[#46A8F9]" style={{ color: NAVY }} />
          <div className="flex gap-2 pt-1">
            <button onClick={() => setMode('search')} className="flex-1 py-2.5 rounded-xl border-[1.5px] border-gray-200 text-[12px] font-semibold text-gray-500">{t('pharmacy.backToSearch')}</button>
            <button onClick={handleManualSave} disabled={!manualName.trim()} className="flex-1 py-2.5 rounded-xl text-[12px] font-semibold text-white transition-colors" style={{ backgroundColor: manualName.trim() ? BLUE : '#E2E8F0' }}>{t('pharmacy.savePharmacy')}</button>
          </div>
        </div>
      </div>
    )
  }

  // ===== SEARCH STATE =====
  return (
    <div className="bg-white rounded-2xl border-[1.5px] border-gray-200 overflow-hidden">
      <div className="flex items-center gap-2 px-3.5 py-2.5 border-l-[3px] border-l-[#46A8F9] bg-[#E9F8FF]">
        <Pill size={15} className="text-[#46A8F9]" />
        <h4 className="text-[12px] font-bold uppercase tracking-[0.5px] text-[#15354F]">{t('pharmacy.findYourPharmacy')}</h4>
      </div>

      <div className="p-3.5 space-y-3">
        {/* Search Input */}
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2">
            {isSearching ? (
              <OrellaSpinner size={16} />
            ) : (
              <Search size={16} className="text-gray-400" />
            )}
          </div>
          <input
            type="text"
            value={query}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search by name, address, or ZIP code..."
            className="w-full pl-10 pr-4 py-3 rounded-xl border-[1.5px] border-gray-200 text-[13px] placeholder-gray-300 focus:outline-none focus:border-[#46A8F9] transition-colors"
            style={{ color: NAVY }}
          />
          {query && (
            <button onClick={() => { setQuery(''); setResults([]) }} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-300 active:text-gray-500">
              <X size={14} />
            </button>
          )}
        </div>

        {error && <p className="text-[11px] text-amber-600">{error}</p>}

        {/* Results */}
        {results.length > 0 && (
          <div className="space-y-1 max-h-[240px] overflow-y-auto -mx-1 px-1">
            {results.map((place, i) => (
              <button
                key={place.placeId || i}
                onClick={() => selectPlace(place)}
                className="w-full text-left p-3 rounded-xl border-[1.5px] border-gray-100 active:border-[#46A8F9] active:bg-[#E9F8FF] transition-colors flex items-start gap-2.5"
              >
                <div className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                  <Pill size={16} className="text-green-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[12.5px] font-semibold truncate" style={{ color: NAVY }}>{place.name}</p>
                  <p className="text-[11px] text-gray-400 truncate">{place.address}</p>
                </div>
                <ChevronRight size={14} className="text-gray-300 flex-shrink-0 mt-1" />
              </button>
            ))}
          </div>
        )}

        {query.length >= 3 && !isSearching && results.length === 0 && (
          <p className="text-[11px] text-gray-400 text-center py-2">{t('pharmacy.noPharmaciesFoundTryA')}</p>
        )}

        {/* Manual entry option */}
        <button
          onClick={() => setMode('manual')}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border-[1.5px] border-dashed border-gray-200 text-[12px] font-semibold text-gray-400 active:bg-gray-50"
        >
          <Plus size={14} />
          {t('pharmacy.enterManually', 'Enter pharmacy manually')}
        </button>

        {/* Skip — hidden where a pharmacy is required (e.g. case send) */}
        {allowSkip && (
          <button onClick={() => onSave?.(null)} className="w-full text-center text-[11px] text-gray-400 font-medium py-0.5">
            {t('pharmacy.skipAddLater', "Skip — I'll add this later")}
          </button>
        )}
      </div>
    </div>
  )
}
